import { Box, Stack, Typography, Chip, IconButton, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import dayjs, { Dayjs } from "dayjs";
import { ILocalInfo } from "../../interfaces/IDoctor";

export type SlotStatus = "AVAILABLE" | "BOOKED" | "UNAVAILABLE";

export type SlotSource = "LOCAL" | "GOOGLE";

export interface DoctorSlot {
  start: string; // ISO
  end: string;   // ISO
  status: SlotStatus;
  scheduleId?: string; // schedule ao qual este slot pertence
  // Informações opcionais do agendamento, quando o slot estiver BOOKED
  appointmentId?: string;
  appointmentStatus?: string;
  patientId?: string | null;
  patientName?: string | null;
  // Metadados opcionais para exibir origem/título (ex.: eventos do Google)
  source?: SlotSource;
  title?: string | null;
}

interface DoctorDayCalendarProps {
  date: Dayjs;
  slots: DoctorSlot[];
  onSelectTime?: (dateTime: Dayjs, slot: DoctorSlot | null) => void;
  onOpenCalendar?: () => void;
  onChangeDate?: (date: Dayjs) => void;
  onAppointmentClick?: (slot: DoctorSlot) => void;
  locals?: ILocalInfo[];
  selectedLocalId?: string | null;
  onChangeLocal?: (id: string) => void;
}

// Gera labels de horário em intervalos fixos para a visão diária
function generateTimeLabels(startHour = 8, endHour = 18, stepMinutes = 30) {
  const labels: string[] = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      labels.push(String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0"));
    }
  }
  return labels;
}

export function DoctorDayCalendar({
  date,
  slots,
  onSelectTime,
  onOpenCalendar,
  onChangeDate,
  onAppointmentClick,
  locals,
  selectedLocalId,
  onChangeLocal,
}: DoctorDayCalendarProps) {
  const labels = generateTimeLabels(8, 18, 30);

  // Se existirem locais, garante que sempre haja um local selecionado visualmente
  const effectiveSelectedLocalId =
    locals && locals.length
      ? selectedLocalId && locals.some((l) => l.id === selectedLocalId)
        ? selectedLocalId
        : locals[0].id
      : "";

  console.log("[AGENDA] labels:", labels);
  console.log("[AGENDA] slots recebidos em DoctorDayCalendar:", slots);

  // semana visível no cabeçalho (segunda a domingo, em português)
  let startOfWeek = date.startOf("week"); // pode cair em domingo dependendo da locale
  // se cair em domingo (0), ajusta para segunda (1)
  if (startOfWeek.day() === 0) {
    startOfWeek = startOfWeek.add(1, "day");
  }

  const weekDays: Dayjs[] = Array.from({ length: 7 }).map((_, index) =>
    startOfWeek.add(index, "day")
  );

  const weekdayLabelsPt = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const getSlotForLabel = (label: string): DoctorSlot | null => {
    const [hour, minute] = label.split(":").map(Number);
    const labelStart = date.hour(hour).minute(minute).second(0).millisecond(0);

    const found =
      slots.find((s) => {
        const slotStart = dayjs(s.start);
        // compara por minuto, ignorando milissegundos/diferenças mínimas
        return slotStart.isSame(labelStart, "minute");
      }) || null;

    if (found) {
      console.log(
        "[AGENDA] label",
        label,
        "casou com slot.start=",
        found.start
      );
    }

    return found;
  };

  const formatHeaderDate = () => {
    return date.format("DD/MM/YYYY");
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="#133859">
            Agenda
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatHeaderDate()} visualize horários disponíveis e agendados
          </Typography>

          {/* Navegação por dias da semana */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              mt: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={() => onChangeDate && onChangeDate(startOfWeek.add(-7, "day"))}
              sx={{
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
                "&:hover": { backgroundColor: "#f5f5f5" },
                flexShrink: 0,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flex: 1, justifyContent: "space-between" }}
            >
              {weekDays.map((day) => {
                const isSelected = day.isSame(date, "day");
                const isDifferentMonth = !day.isSame(date, "month");
                const weekdayLabel = weekdayLabelsPt[day.day()];

                return (
                  <Box
                    key={day.format("YYYY-MM-DD")}
                    onClick={() => onChangeDate && onChangeDate(day)}
                    sx={{
                      px: { xs: 0.6, sm: 1.2 },
                      py: 0.6,
                      borderRadius: 2,
                      cursor: onChangeDate ? "pointer" : "default",
                      border: "1px solid " + (isSelected ? "#1976d2" : "#e0e0e0"),
                      backgroundColor: isSelected ? "#1976d2" : "#ffffff",
                      color: isSelected ? "#ffffff" : isDifferentMonth ? "#9e9e9e" : "#133859",
                      flex: { xs: 1, sm: 0 },
                      minWidth: { xs: 0, sm: 44 },
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        lineHeight: 1,
                        fontSize: { xs: 10, sm: 11 },
                      }}
                    >
                      {weekdayLabel}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: 11, sm: 12 }, lineHeight: 1.1, fontWeight: 500 }}
                    >
                      {day.format("DD")}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            <IconButton
              size="small"
              onClick={() => onChangeDate && onChangeDate(startOfWeek.add(7, "day"))}
              sx={{
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#ffffff",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: "row", md: "row" }}
          spacing={1}
          sx={{ mt: { xs: 1, md: 0 } }}
          alignItems="center"
        >
          {locals && locals.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="doctor-day-local-select-label">Local</InputLabel>
              <Select
                labelId="doctor-day-local-select-label"
                value={effectiveSelectedLocalId}
                label="Local"
                onChange={(e) =>
                  onChangeLocal && onChangeLocal(e.target.value as string)
                }
              >
                {locals.map((local) => (
                  <MenuItem key={local.id} value={local.id}>
                    {local.localName || local.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <IconButton
            size="small"
            onClick={onOpenCalendar}
            sx={{
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              backgroundColor: "#ffffff",
              ml: { xs: 0, md: 1 },
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <CalendarMonthIcon fontSize="small" color="primary" />
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          background: "linear-gradient(180deg, #fdfdff 0%, #f7f9fc 100%)",
        }}
      >
        <Stack direction="row" sx={{}}>
          {/* Coluna de horários */}
          <Box
            sx={{
              width: 80,
              borderRight: "1px solid #e0e0e0",
              backgroundColor: "#fafbff",
            }}
          >
            {labels.map((label) => (
              <Box
                key={label}
                sx={{
                  height: 40,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-end",
                  pr: 1,
                  pt: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Coluna de slots */}
          <Box sx={{ flex: 1 }}>
            {labels.map((label, index) => {
              const slot = getSlotForLabel(label);

              let background = "transparent";
              let borderLeft = "1px solid #f0f0f0";
              let content: React.ReactNode = null;

              if (slot?.status === "BOOKED") {
                background = "rgba(25, 118, 210, 0.08)";
                borderLeft = "3px solid #1976d2";
                content = (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip size="small" label="Agendado" color="primary" variant="filled" />
                    <Typography variant="body2" color="#133859">
                      {slot.patientName || slot.title || "Consulta marcada"}
                    </Typography>
                  </Stack>
                );
              } else if (slot?.status === "AVAILABLE") {
                background = "rgba(56, 142, 60, 0.06)";
                borderLeft = "3px solid #2e7d32";
                content = (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip size="small" label="Disponível" color="success" variant="outlined" />
                    <Typography variant="body2" color="#2e7d32">
                      Horário livre
                    </Typography>
                  </Stack>
                );
              } else if (slot?.status === "UNAVAILABLE" && slot?.source === "GOOGLE") {
                // Blocos ocupados por eventos do Google Calendar: exibimos o nome do evento
                background = "rgba(244, 67, 54, 0.04)";
                borderLeft = "3px solid #f44336";
                content = (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip size="small" label="Google" color="error" variant="outlined" />
                    <Typography variant="body2" color="#b71c1c">
                      {slot.title || "Compromisso Google"}
                    </Typography>
                  </Stack>
                );
              } else {
                // UNAVAILABLE ou sem slot cadastrado de outra origem: apenas linha discreta
                content = null;
              }

              const [hour, minute] = label.split(":").map(Number);
              const dateTime = date.hour(hour).minute(minute).second(0).millisecond(0);

              const clickable = Boolean(onSelectTime) || (slot?.status === "BOOKED" && !!onAppointmentClick);

              const handleClick = () => {
                if (slot?.status === "BOOKED" && onAppointmentClick) {
                  onAppointmentClick(slot);
                  return;
                }
                if (onSelectTime) {
                  onSelectTime(dateTime, slot || null);
                }
              };

              return (
                <Box
                  key={label}
                  onClick={clickable ? handleClick : undefined}
                  sx={{
                    height: 40,
                    borderTop: index === 0 ? "none" : "1px dashed #eeeeee",
                    borderLeft,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: background,
                    cursor: clickable ? "pointer" : "default",
                    "&:hover": clickable
                      ? {
                          backgroundColor:
                            background !== "transparent"
                              ? background
                              : "rgba(0, 0, 0, 0.02)",
                        }
                      : undefined,
                  }}
                >
                  {content}
                </Box>
              );
            })}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}